import { Paper } from "@mui/material";
import Card from "components/Dashboard/Card";
import { formatMoney, formatPercent } from "./Utils";

export default function FinancialMetrics(props) {
  let rates = {}
  props.mortgageRates.forEach(record => {
    const key = `${record[0]}-${record[2]}`
    if (!rates[key]) {
      rates[key] = []
    }
    rates[key].push([record[3], record[4]]);
  })
  const rates30yr = []
  const rates15yr = []
  Object.entries(rates).forEach(([key, val]) => {
    const month = parseInt(key.split('-')[0])
    const year = parseInt(key.split('-')[1])
    const _30yr = val.map(_ => _[0])
    const _15yr = val.map(_ => _[1])
    rates30yr.push([month - 1, year, _30yr.reduce((x, v) => x + v, 0) / _30yr.length / 100])
    rates15yr.push([month - 1, year, _15yr.reduce((x, v) => x + v, 0) / _15yr.length / 100])
  });

  const t = props.estatedProperty;
  const mortgagePaymentEquation = (
    principalLoanAmount,
    annualInterestRate,
    numberOfMonths
  ) => {
    const monthlyInterestRate = annualInterestRate / 12;
    const a = Math.pow(1 + monthlyInterestRate, numberOfMonths);
    return (principalLoanAmount * monthlyInterestRate * a) / (a - 1);
  };
  const outstandingLoanBalanceEquation = (
    principalLoanAmount,
    annualInterestRate,
    numberOfMonths,
    numberOfMonthsPast
  ) => {
    const monthlyInterestRate = annualInterestRate / 12;
    const a = Math.pow(1 + monthlyInterestRate, numberOfMonths);
    const b = Math.pow(1 + monthlyInterestRate, numberOfMonthsPast);
    return (principalLoanAmount * (a - b)) / (a - 1);
  };
  const monthDiff = (dateFrom, dateTo) => {
    return (
      dateTo.getMonth() -
      dateFrom.getMonth() +
      12 * (dateTo.getFullYear() - dateFrom.getFullYear())
    );
  };

  let monthlyMortgageEstimate;
  let outstandingMortgageEstimate;
  let isPaidOff = false;
  const latestLoanInfo = t.latestLoanInfo;
  if (latestLoanInfo && latestLoanInfo.original_contract_date) {
    const loanDate = latestLoanInfo.original_contract_date;
    const loanMonth = loanDate.getMonth();
    const loanYear = loanDate.getFullYear();
    // default to 30-yr
    let estimatedInterestRate =
      rates30yr.find((_) => _[0] === loanMonth && _[1] === loanYear) ||
      rates30yr[rates30yr.length - 1];
    let mortgageNumMonths = 30 * 12;
    if (latestLoanInfo.due_date) {
      mortgageNumMonths = monthDiff(loanDate, latestLoanInfo.due_date);
      if (mortgageNumMonths < 16 * 12) {
        // If duration is less than 16-years, use 15-year rates for better estimate
        estimatedInterestRate =
          rates15yr.find((_) => _[0] === loanMonth && _[1] === loanYear) ||
          rates15yr[rates15yr.length - 1];
      }
    }
    let loanAmount = latestLoanInfo.loan_amount;
    if (latestLoanInfo.due_date) {
      if (latestLoanInfo.due_date < new Date()) {
        // if the due date has past, then assume loan is $0
        loanAmount = 0;
        isPaidOff = true;
      }
    }
    monthlyMortgageEstimate = mortgagePaymentEquation(
      loanAmount,
      estimatedInterestRate[2],
      mortgageNumMonths
    );
    outstandingMortgageEstimate = outstandingLoanBalanceEquation(
      loanAmount,
      estimatedInterestRate[2],
      mortgageNumMonths,
      monthDiff(loanDate, new Date())
    );
  }

  let taxesEstimate;
  if (t.taxes.length) {
    const tax = t.taxes[0];
    if (tax.amount) {
      taxesEstimate = tax.amount;
    }
  }

  if (!taxesEstimate && t.scraper.estimated_annual_tax) {
    taxesEstimate = t.scraper.estimated_annual_tax;
  }

  let estimatedPremium;
  if (
    t.estimated_premium &&
    t.estimated_premium["2500"] &&
    t.estimated_premium["2500"].total
  ) {
    estimatedPremium =
      Math.min(
        t.estimated_premium["2500"].total,
        t.estimated_premium["5000"].total
      ) / 12;
  }

  const estimatedRent = t.estimatedRent;
  let estimatedDscr;
  let estimatedNoi;
  let estimatedCashflow;
  let income;
  let expenses;
  if (estimatedRent && taxesEstimate && estimatedPremium) {
    income = estimatedRent * 12;
    expenses =
      estimatedRent * 0.06 + // vacancy
      25 + // tenant screening
      0.44 * estimatedRent + // leasing fees
      0.08 * income + // property management fees
      1.5 * estimatedRent + // repairs and maintenance
      estimatedPremium + // insurance estimate
      taxesEstimate + // property taxes
      (t.scraper.monthly_hoa || 0) * 12 + // hoa
      250; // professional services
    estimatedNoi = income - expenses;
    const debtService = monthlyMortgageEstimate * 12;
    estimatedDscr = estimatedNoi / debtService;
    estimatedCashflow = estimatedNoi - debtService;
  }

  const IRRCalc = (arr) => {
    let min = -1.0;
    let max = 1.0;
    let guest, NPV;
    let c = 0;
    do {
      guest = (min + max) / 2;
      NPV = 0;
      for (let j = 0; j < arr.length; j++) {
        NPV += arr[j] / Math.pow(1 + guest, j);
      }
      if (NPV > 0) {
        min = guest;
      } else {
        max = guest;
      }
      c += 1;
      if (c >= 100) {
        return guest;
      }
    } while (Math.abs(NPV) > 0.000001);
    return guest;
  };
  const property_inflation = 0.03;
  const income_inflation = 0.03;
  const expense_inflation = 0.015;
  const purchase_closing_costs = 0.0075;
  const sale_closing_costs = 0.05;
  const ltv = 0.8;

  const computeIRR = (
    propValue,
    asOf,
    adjustedIncome,
    adjustedExpenses,
    loanTerm,
    numYears
  ) => {
    const month = asOf.getMonth();
    const year = asOf.getFullYear();
    const estimatedInterestRate =
      rates30yr.find((_) => _[0] === month && _[1] === year) ||
      rates30yr[rates30yr.length - 1];
    const i = [...Array(numYears)].map(
      (_, year) => adjustedIncome * Math.pow(1 + income_inflation / 1, 1 * year)
    );
    const e = [...Array(numYears)].map(
      (_, year) =>
        adjustedExpenses * Math.pow(1 + expense_inflation / 1, 1 * year)
    );
    const adjustedAnnualMortgageEstimate =
      mortgagePaymentEquation(
        propValue * ltv,
        estimatedInterestRate[2],
        loanTerm * 12
      ) * 12;
    const vals = [
      -(propValue * (1 - ltv) + purchase_closing_costs * propValue),
      ...[...Array(numYears)].map(
        (_, year) => i[year] - e[year] - adjustedAnnualMortgageEstimate
      ),
    ];
    const futureSaleDate = new Date(
      asOf.getFullYear() + numYears + 1,
      asOf.getMonth(),
      asOf.getDate()
    );
    const outstandingLoanBalance = outstandingLoanBalanceEquation(
      propValue * ltv,
      estimatedInterestRate[2],
      loanTerm * 12,
      monthDiff(asOf, futureSaleDate)
    );
    const newPropValue =
      propValue * Math.pow(1 + property_inflation / 1, 1 * numYears);
    vals[numYears] = vals[numYears] - sale_closing_costs * newPropValue;
    vals[numYears] = vals[numYears] - outstandingLoanBalance;
    vals[numYears] = vals[numYears] + newPropValue;
    return IRRCalc(vals);
  };

  let todaysIrr;
  if (t.property_info.estimated_value && income && expenses) {
    const propValue = t.property_info.estimated_value;
    const asOf = new Date();
    todaysIrr = [
      computeIRR(propValue, asOf, income, expenses, 30, 3),
      computeIRR(propValue, asOf, income, expenses, 30, 5),
      computeIRR(propValue, asOf, income, expenses, 30, 7),
      computeIRR(propValue, asOf, income, expenses, 30, 10),
    ];
  }

  let saleIrr;
  if (latestLoanInfo && latestLoanInfo.sale_price && income && expenses) {
    // Use the sale date and price and discount today's income and expense projections
    // using the inflation factor
    const propValue = latestLoanInfo.sale_price;
    const asOf = latestLoanInfo.original_contract_date;
    const yearsAgo = Math.round(monthDiff(asOf, new Date()) / 12);
    const adjustedIncome =
      1 / (Math.pow(1 + income_inflation / 1, 1 * yearsAgo) / income);
    const adjustedExpenses =
      1 / (Math.pow(1 + expense_inflation / 1, 1 * yearsAgo) / expenses);
    saleIrr = [
      computeIRR(propValue, asOf, adjustedIncome, adjustedExpenses, 30, 3),
      computeIRR(propValue, asOf, adjustedIncome, adjustedExpenses, 30, 5),
      computeIRR(propValue, asOf, adjustedIncome, adjustedExpenses, 30, 7),
      computeIRR(propValue, asOf, adjustedIncome, adjustedExpenses, 30, 10),
    ];
  }

  const data = {
    "LTV at PP":
      latestLoanInfo && latestLoanInfo.sale_price
        ? formatPercent(latestLoanInfo.loan_amount / latestLoanInfo.sale_price)
        : undefined,
    "Estimated Loan to Current Value": isPaidOff
      ? "Paid Off"
      : formatPercent(outstandingMortgageEstimate / t.estimatedValue),
    "Estimated Monthly Rent": formatMoney(estimatedRent),
    [`Estimated Monthly Mortgage + Taxes + Insurance ${
      isPaidOff ? " (Paid Off)" : ""
    }`]: formatMoney(
      Math.round(
        monthlyMortgageEstimate + taxesEstimate / 12 + estimatedPremium / 12
      )
    ),
    "Estimated Annual DSCR": isPaidOff
      ? "Paid Off"
      : estimatedDscr
      ? estimatedDscr.toFixed(2)
      : undefined,
    "Estimated Annual NOI": estimatedNoi
      ? formatMoney(Math.round(estimatedNoi))
      : undefined,
    "Estimated Annual Cash Flow": estimatedCashflow
      ? formatMoney(Math.round(estimatedCashflow))
      : undefined,
    "IRR as of Today (3yr, 5yr, 7yr, 10yr)": todaysIrr
      ? todaysIrr.map((_) => `${formatPercent(_)}`).join(", ")
      : undefined,
    "IRR as of Sale (3yr, 5yr, 7yr, 10yr)": saleIrr
      ? saleIrr.map((_) => `${formatPercent(_)}`).join(", ")
      : undefined,
  };
  return (
    <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
      <Card
        label="Financial Metrics"
        activeKey="financial_metrics"
        row={data}
      />
    </Paper>
  );
}
